<template>
  <div class="settlement-table">
    <span class="customer-badge"
      >Podopieczny<strong class="badge-name">{{
        table.customerName
      }}</strong></span
    >
    <label class="ready-control" :class="{ 'ready-control-disabled': disabled }" v-if="!readonly">
      <input
        type="checkbox"
        :checked="table.isReady"
        :disabled="disabled"
        @change="onToggleReady($event)"
      />
      <span>Gotowe</span>
    </label>
    <span v-else class="ready-control ready-readonly">{{
      table.isReady ? "Gotowe" : "Niegotowe"
    }}</span>
    <table>
      <thead>
        <tr class="column-headers">
          <td>ID</td>
          <td>Podopieczny</td>
          <td>Wydział</td>
          <td>Data</td>
          <td>Il. godz. zleconych</td>
          <td>Il. godz. wykonanych</td>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(day, index) in table.days" :key="day.orderDayId">
          <td class="id-cell">{{ day.orderDayId }}</td>
          <td>{{ table.customerName }}</td>
          <td>{{ table.departmentName }}</td>
          <td>{{ formatDate(day.date) }}</td>
          <td>{{ day.ordered }}</td>
          <td>
            <span v-if="readonly || table.isReady">{{
              day.made == null ? "" : day.made
            }}</span>
            <input
              v-else
              class="made-hours-input"
              :id="`settlement-made-${day.orderDayId}`"
              type="number"
              min="0"
              step="1"
              :max="day.ordered"
              :value="day.made == null ? '' : day.made"
              :disabled="disabled"
              @input="onInput(day, $event)"
              @keypress.enter="moveToNextInput(index)"
            />
          </td>
        </tr>
      </tbody>
      <tfoot>
        <tr class="summary-row">
          <td colspan="4" style="text-align: right; font-weight: bold">SUMA</td>
          <td style="font-weight: bold">{{ sumOrdered }}</td>
          <td style="font-weight: bold">{{ sumMade }}</td>
        </tr>
      </tfoot>
    </table>
  </div>
</template>

<script>
import shared from "../../../shared";

export default {
  name: "SettlementOrderTable",
  props: {
    table: { type: Object, required: true },
    readonly: { type: Boolean, default: false },
    // Blokuje checkbox "Gotowe" i pola godzin na czas zapisu/przekazywania.
    disabled: { type: Boolean, default: false },
  },
  emits: ["update-hours", "toggle-ready"],
  computed: {
    sumOrdered() {
      return this.table.days.reduce(
        (sum, d) => sum + Number(d.ordered || 0),
        0
      );
    },
    sumMade() {
      return this.table.days.reduce((sum, d) => sum + Number(d.made || 0), 0);
    },
  },
  methods: {
    formatDate(date) {
      return shared.dateToDayMonthYearFormat(date);
    },
    onInput(day, event) {
      let raw = event.target.value;
      if (raw === "") {
        this.$emit("update-hours", {
          orderId: this.table.orderId,
          orderDayId: day.orderDayId,
          value: null,
        });
        return;
      }
      let value = Number(raw);
      if (isNaN(value) || value < 0) {
        value = 0;
      }
      // Hours are integers in the DB (and the backend DTO is @IsInt).
      value = Math.floor(value);
      // Cannot exceed ordered hours for the day.
      if (value > day.ordered) {
        value = day.ordered;
      }
      // Reflect any normalization back into the field.
      if (String(value) !== raw) {
        event.target.value = value;
      }
      this.$emit("update-hours", {
        orderId: this.table.orderId,
        orderDayId: day.orderDayId,
        value,
      });
    },
    onToggleReady(event) {
      this.$emit("toggle-ready", {
        orderId: this.table.orderId,
        isReady: event.target.checked,
      });
    },
    moveToNextInput(index) {
      const next = this.table.days[index + 1];
      if (next) {
        const el = document.getElementById(
          `settlement-made-${next.orderDayId}`
        );
        if (el) el.focus();
      }
    },
  },
};
</script>

<style scoped>
/* Rezerwa u góry na badge wystający nad tabelę; pozycjonowanie absolutne badge/Gotowe
   gwarantuje przyklejenie do tabeli niezależnie od globalnych styli (np. bootstrap
   label { margin-bottom }). */
.settlement-table {
  position: relative;
  margin-bottom: 28px;
  padding-top: 30px;
}
/* Zaokrąglony gradientowy badge - nachodzi 2px na górę nagłówka, żeby na styku nie
   było szczeliny; leży na tabeli (z-index) i ma większy radius jak w Canvie. */
.customer-badge {
  position: absolute;
  left: 5px;
  top: 0;
  height: 32px;
  z-index: 1;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  background: linear-gradient(90deg, #fb8928, #f14c52);
  color: #fff;
  padding: 0 22px;
  border-radius: 14px 14px 0 0;
  font-size: 12px;
  text-transform: uppercase;
  white-space: nowrap;
}
/* Odstęp między "Podopieczny" a nazwą przez margines - niezależny od tego, jak
   kompilator Vue traktuje spacje/encje w szablonie. */
.customer-badge .badge-name {
  margin-left: 6px;
}
.ready-control {
  position: absolute;
  right: 0;
  top: 0;
  height: 30px;
  margin: 0;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-weight: 600;
  color: #090741;
  font-size: 13px;
}
.ready-readonly {
  color: #8a8a8a;
  cursor: default;
}
.ready-control-disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.ready-control input[type="checkbox"]:disabled {
  cursor: not-allowed;
}
.made-hours-input:disabled {
  background: #f2f2f2;
  cursor: not-allowed;
}
/* Gradientowy checkbox - ten sam wzorzec co w DodFiltersCheckboxes. */
.ready-control input[type="checkbox"] {
  position: relative;
  width: 20px;
  height: 20px;
  appearance: none;
  -webkit-appearance: none;
  background-color: transparent;
  outline: none;
  margin: 0;
  cursor: pointer;
}
.ready-control input[type="checkbox"]::after {
  content: "";
  width: 18px;
  height: 18px;
  position: absolute;
  top: 0;
  left: 0;
  background: white;
  border: 1px solid #090741;
  border-radius: 4px;
}
.ready-control input[type="checkbox"]:checked::after {
  content: "✔";
  display: inline-block;
  border: unset;
  background: linear-gradient(
    90deg,
    rgba(251, 134, 42, 1) 0%,
    rgba(241, 76, 82, 1) 49%,
    rgba(229, 8, 129, 1) 100%
  );
  font-size: 14px;
  line-height: 18px;
  text-align: center;
  color: white;
}
table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 12px;
  margin: 0;
}
/* Jeden ciągły gradient nagłówka kolumn (brandowy). */
thead {
  background: linear-gradient(90deg, #fb8928, #e50582);
}
thead td {
  background: transparent;
  color: #fff;
}
.column-headers td {
  padding: 12px 8px;
  font-weight: 600;
}
.column-headers td:last-child {
  border-top-right-radius: 14px;
}
tbody td {
  padding: 6px 8px;
  border-bottom: 1px solid #eee;
}
.id-cell {
  font-size: 10px;
}
.made-hours-input {
  width: 42px;
  border-radius: 8px;
  outline: none;
  text-align: center;
  border: 1px solid rgba(203, 203, 203, 0.6);
}
.made-hours-input::-webkit-outer-spin-button,
.made-hours-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.summary-row td {
  border-top: 2px solid #333;
  padding-top: 8px;
}
</style>
